import { BigNumber } from 'bignumber.js'
import { Abi, Contract, transaction, AccountInvocationItem, Call } from 'starknet'
import { SwapQuote, CLPool, Transaction } from '../atoms'
import ammV3Abi from '../contractsAbi/amm_v3.json'
import erc20Abi from '../contractsAbi/ERC20_Mintable.json'
import { Network } from './token.service'
import { getProvider, numberToCallParam, walletAddress } from './amm'
import { fromUint256, toDisplay, toUint256, toWei } from '../model/tokens/helper'
import { StarknetWindowObject } from 'get-starknet'
import { getFactoryAddress } from '../model/clpool/helper'

export const MAX_UINT_128 = '340282366920938463463374607431768211455'
export const getAMMV3Contract = (networkName: string): string => {
    if (networkName?.includes('mainnet')) return '0x1114c7103e12c2b2ecbd3a2472ba9c48ddcbf702b1c242dd570057e26212111'
    if (networkName?.includes('sepolia')) return '0x02354515660599bf97a21a2de195785ef8f407399ad910b98f8d15683cde193c'
    return '0x0436924c4ed166d3c283d516adc424976cfccba108e3a0e3f3fc1ef319e23aa7' //'0x74415f18bcc39b26f31b67796270d13ea27db74885bd1fffd9bf63cf816e758'
}

export async function getContract(network: Network) {
    return new Contract(ammV3Abi as Abi, getAMMV3Contract(network), await getProvider(network))
}

export async function getERC20Contract(network: Network, address: string) {
    return new Contract(erc20Abi as Abi, address, await getProvider(network))
}

export const swapV3 = async (network: Network, quote: SwapQuote, slippagePercent: number, token1: string, token2: string): Promise<any> => {
    const starknet = await getStarknet()

    let t1amount = quote.token1AmountWei ?? '0'
    let t2amount = quote.token2AmountWei ?? '0'
    if (!quote.forwardSwap) {
        const tmp = t1amount
        t1amount = t2amount
        t2amount = tmp
    }

    // console.log('YSYS ', quote.forwardSwap, ' , ', quote.exactInput, ' ,', ' t1=', quote.pool.token1.symbol, ' t2=', quote.pool.token2.symbol)
    const tokenFrom = quote.forwardSwap ? quote.pool.token1 : quote.pool.token2
    const sqrtPriceLimitX96 = sqrtPriceLimitX96BySlippage(t1amount, t2amount, slippagePercent, quote.forwardSwap)
    // increase allowance of token1 if doing an exact outout swap - by slippage
    if (!quote.exactInput) {
        t1amount = BigNumber(t1amount)
            .multipliedBy(1 + slippagePercent)
            .plus(5)
            .toFixed(0)
    }
    const amm = getFactoryAddress(network)
    const calls: Array<Call> = [
        {
            contractAddress: tokenFrom.address,
            entrypoint: 'approve',
            calldata: [amm, ...toUint256(t1amount)]
        },
        {
            entrypoint: 'swap',
            contractAddress: amm,
            calldata: [
                quote.pool.address,
                quote.forwardSwap ? '1' : '0',
                ...toUint256(quote.exactInput ? t1amount : t2amount),
                quote.exactInput ? '1' : '0',
                ...toUint256(sqrtPriceLimitX96)
                // for limited swap: number of steps
                // '600',
                // '1'
            ]
        }
    ]

    if (FAIL_ALL_TXS) {
        calls.push({
            contractAddress: FAIL_CONTRACT,
            entrypoint: 'fail',
            calldata: []
        })
    }

    // @ts-ignore
    let tx = await starknet.account.execute(calls)
    // YSYS TODO execute the calls

    const transaction: Transaction = {
        hash: tx.transaction_hash,
        network,
        status: 'new',
        token1: token1,
        token1Amount: t1amount,
        token2: token2,
        token2Amount: t2amount,
        type: 'swap',
        dismiss: false
    }

    return transaction
}

export async function getStarknet(): Promise<StarknetWindowObject | undefined> {
    const starknet = window.getStarknet?.()
    if (starknet?.isConnected) return starknet
    await starknet?.enable({ starknetVersion: 'v5' })
    return starknet
}

const dummy_account = '0x0105311736091d47be17ba84e00c7453bf22d759a496c82fd6a0084fab6d3153'
const dummy_account_sepolia = '0x01ee7fa5a34408279669a58d1e5df0f45d714b92d6527fca5b77f4c85ec72999'

export const simulateSwapV3 = async (
    network: Network,
    pool: CLPool,
    forwardSwap: boolean,
    amount: string,
    exactInput: boolean,
    sqrtPriceLimitX96: string
): Promise<SwapQuote | undefined> => {
    const starknet = await getStarknet()
    // ------------------------------------------
    // Omry: Is this the right code? null if no active but throw exception if not connected?
    let activeAccount: string = walletAddress()
    if (!activeAccount) {
        return undefined
    }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------

    const tokenFrom = forwardSwap ? pool.token1 : pool.token2
    const tokenTo = forwardSwap ? pool.token2 : pool.token1
    const amountWei = toWei(amount, exactInput ? tokenFrom : tokenTo)
    const amm = getFactoryAddress(network)
    const calls = transaction.fromCallsToExecuteCalldata([
        {
            entrypoint: 'current_sqrt_price',
            contractAddress: amm,
            calldata: [pool.address]
        },
        {
            contractAddress: tokenFrom.address,
            entrypoint: 'approve',
            calldata: [amm, MAX_UINT_128, MAX_UINT_128]
        },
        {
            entrypoint: 'swap1',
            contractAddress: amm,
            calldata: [
                pool.address,
                forwardSwap ? '1' : '0',
                ...toUint256(amountWei),
                exactInput ? '1' : '0',
                ...toUint256(sqrtPriceLimitX96),
                // for swap1: number of steps
                '600',
                '1',
                '1'
            ]
        },
        {
            entrypoint: 'current_sqrt_price',
            contractAddress: amm,
            calldata: [pool.address]
        }
    ])

    // we use dummy_account which is deployed on mainnet, testnet
    // and has a nonce of 1 (or 2 on sepolia)
    const da = network.includes('sepolia') ? dummy_account_sepolia : dummy_account
    const nonce = network.includes('sepolia') ? 2 : 1

    const invocation: AccountInvocationItem = {
        signature: [],
        // @ts-ignore
        type: 'INVOKE_FUNCTION',
        nonce: nonce,
        entrypoint: '__execute__',
        contractAddress: da, //starknet.account.address,
        calldata: calls,
        version: '340282366920938463463374607431768211457'
    }

    const isRpc = true
    const provider = await getProvider(network, !isRpc)

    const response = await provider.getSimulateTransaction([invocation], {
        blockIdentifier: 'pending',
        skipValidate: true
    })

    // @ts-ignore
    const res = isRpc ? response[0].transaction_trace.execute_invocation.result : response[0].transaction_trace.function_invocation.result
    // for limited_swap: token1amount moves
    const token1Amountwei = fromUint256(res[res.length - 8], res[res.length - 7])
    // for limited swap
    const token2Amountwei = fromUint256(res[res.length - 5], res[res.length - 4]) // returns in 18 decimals?
    // const steps = res[6]
    // console.log('YSYS NUM STEPS: ', steps)
    const startPrice = fromUint256(res[res.length - 11], res[res.length - 10])
        .dividedBy(BigNumber(2).pow(96))
        .pow(2)
        .toString()
    const endPrice = fromUint256(res[res.length - 2], res[res.length - 1])
        .dividedBy(BigNumber(2).pow(96))
        .pow(2)
        .toString()

    let latestBlock = undefined
    if (isRpc) {
        // @ts-ignore
        const swap_events = response[0]?.transaction_trace?.execute_invocation?.calls[0]?.calls[2]?.events
        if (swap_events && swap_events.length >= 2) {
            if (swap_events[0].keys[0] === '0x276f1ae6132932bcd8d8aedebca8b06b99d1fb69b9067f6c6bf9de59ce34db8') {
                latestBlock = new BigNumber(swap_events[0].data[0]).toNumber()
            }
        }
    } else {
        // @ts-ignore
        const swap_events = response[0]?.transaction_trace?.function_invocation?.internal_calls[0]?.internal_calls[2]?.events
        // const swap_events = response[0]?.transaction_trace?.function_invocation?.internal_calls[0]?.internal_calls[1]?.events
        if (swap_events && swap_events.length >= 2) {
            if (swap_events[0].keys[0] === '0x276f1ae6132932bcd8d8aedebca8b06b99d1fb69b9067f6c6bf9de59ce34db8') {
                latestBlock = new BigNumber(swap_events[0].data[0]).toNumber()
            }
        }
    }

    const quote: SwapQuote = {
        pool: pool,
        token1Amount: toDisplay(token1Amountwei, pool.token1),
        token1AmountWei: token1Amountwei.toFixed(0),
        token2Amount: toDisplay(token2Amountwei, pool.token2),
        token2AmountWei: token2Amountwei.toFixed(0),
        forwardSwap: forwardSwap,
        exactInput: exactInput,
        requestedAmountWei: amountWei,
        blockNumber: latestBlock,
        poolPriceBefore: startPrice,
        poolPriceAfter: endPrice
    }
    // console.log('YSYS quote=', quote)
    return quote
}

export const getSlot0 = async (network: Network): Promise<any> => {
    const starknet = await getStarknet()

    let activeAccount: string = walletAddress()
    if (!activeAccount) {
        return null
    }

    if (!starknet?.isConnected) throw Error('starknet wallet not connected')

    try {
        const response = await (
            await getProvider(network)
        ).callContract({
            entrypoint: 'get_slot0',
            contractAddress: getAMMV3Contract(network) // change to pool contract
        })
        return Number(response.result[2]) - 887272
    } catch (e: any) {
        console.log('error ', e.message)
        return null
    }
}

export const getCurrentTick = async (network: Network, address: string): Promise<any> => {
    try {
        const response = await (
            await getProvider(network)
        ).callContract({
            entrypoint: 'current_tick',
            calldata: [address],
            contractAddress: getFactoryAddress(network)
        })
        return Number(response.result[0]) - 887272
    } catch (e: any) {
        console.log('error ', e.message)
        return null
    }
}

export const getCurrentRatio = async (network: Network, poolAddress: string): Promise<any> => {
    try {
        const response = await (
            await getProvider(network)
        ).callContract({
            entrypoint: 'current_sqrt_price',
            calldata: [poolAddress],
            contractAddress: getFactoryAddress(network)
        })
        const ratio = new BigNumber(response.result[0])
            .plus(new BigNumber(response.result[1]).multipliedBy(new BigNumber(2).pow(new BigNumber(128))))
            .div(new BigNumber(2).pow(new BigNumber(96)))

        return ratio.pow(2)
    } catch (e: any) {
        throw new Error('error getCurrentRatio ' + e.message)
    }
}

export const mint = async (
    network: string,
    recipient: string,
    tickLower: number,
    tickUpper: number,
    amount: number,
    token1: string,
    token2: string,
    token1Amount: string,
    token2Amount: string
): Promise<any> => {
    const starknet = await getStarknet()

    let activeAccount: string = walletAddress()
    if (!activeAccount) {
        return null
    }

    if (!starknet?.isConnected) throw Error('starknet wallet not connected')

    // .callContract  // for getting the number without swapping
    await starknet.account.execute(
        [
            {
                contractAddress: token1,
                entrypoint: 'approve',
                calldata: [numberToCallParam(getAMMV3Contract(network)), numberToCallParam(token1Amount), numberToCallParam(0)]
            },
            {
                contractAddress: token2,
                entrypoint: 'approve',
                calldata: [numberToCallParam(getAMMV3Contract(network)), numberToCallParam(token2Amount), numberToCallParam(0)]
            },
            {
                contractAddress: getAMMV3Contract(network),
                entrypoint: 'mint',
                calldata: [numberToCallParam(recipient), numberToCallParam(tickLower), numberToCallParam(tickUpper), numberToCallParam(amount)]
            }
        ],
        [erc20Abi as Abi, ammV3Abi as Abi]
    )
}

function sqrtPriceLimitX96BySlippage(t1amount: string, t2amount: string, slippagePercent: number, zeroForOne: boolean): string {
    if (zeroForOne) {
        // t1 swapped for t2 in pool order
        // price limit is t2amount/t1amount minus slippage
        return BigNumber(t2amount)
            .dividedBy(BigNumber(t1amount))
            .multipliedBy(1 - slippagePercent)
            .sqrt()
            .multipliedBy(BigNumber(2).pow(96))
            .toFixed(0)
    } else {
        // t2 swapped for t1, (reverse pool order)
        // price limit is t2amount/t1amount minus slippage
        return BigNumber(t1amount)
            .dividedBy(BigNumber(t2amount))
            .multipliedBy(1 + slippagePercent)
            .sqrt()
            .multipliedBy(BigNumber(2).pow(96))
            .toFixed(0)
    }
}

export async function getERC20Details(network: Network, address: string) {
    // const contract = getERC20Contract(netowrk, address);
    try {
        const nameResponse = await (
            await getProvider(network)
        ).callContract({
            entrypoint: 'name',
            contractAddress: address
        })

        const symbolResponse = await (
            await getProvider(network)
        ).callContract({
            entrypoint: 'symbol',
            contractAddress: address
        })

        const decimalsResponse = await (
            await getProvider(network)
        ).callContract({
            entrypoint: 'decimals',
            contractAddress: address
        })

        const name = hexToString(nameResponse.result[0])
        const symbol = hexToString(symbolResponse.result[0])
        const decimals = Number(decimalsResponse.result[0])

        return {
            name,
            symbol,
            decimals,
            address
        }
    } catch (e) {
        return undefined
    }
}

function hexToString(hex: string): string {
    let str = ''

    // Ensure the hex string has an even length
    if (hex.length % 2 !== 0) {
        throw new Error('Invalid hex string. It must have an even length.')
    }

    // Loop through the hex string two characters at a time
    for (let i = 0; i < hex.length; i += 2) {
        // Get the pair of characters representing a byte
        const byte = hex.substr(i, 2)

        // Convert the byte to a decimal number
        const charCode = parseInt(byte, 16)

        // Filter out null bytes
        if (charCode !== 0) {
            // Convert the decimal number to a character and append it to the result string
            str += String.fromCharCode(charCode)
        }
    }

    return str
}

export const FAIL_ALL_TXS: boolean = false
export const FAIL_CONTRACT = '0x05d547138513745f14619ae5f696b4c76c3154a50c31865ee35a1f085217c7dd'
